import { fetcher } from "@certn/api";
import { Link, PrimaryButton, Stack, Text } from "@certn/cascadia";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@certn/cascadia-react";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useApplicationFormContext } from "../../../ApplicationFormContext";
import { OneIdLayoutContext } from "../../../layouts/OneIdLayoutContext";

type Props = {
  optBackInToBiometricId: () => void;
};

export function NonBiometricIdModal(props: Props) {
  const { optBackInToBiometricId } = props;
  const { optedOutOfBiometricId } = useContext(OneIdLayoutContext);
  const { caseId } = useApplicationFormContext();
  const { i18n } = useTranslation();
  const language = i18n.language.split("-")[0];
  const [interacUrl, setInteracUrl] = useState<null | string>(null);
  const [interacUrlLoading, setInteracUrlLoading] = useState(true);
  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );

  function redirectToInterac(interac_url: string) {
    if (isMobile) {
      window.open(interac_url, "_blank");
    } else {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const interacWindowHeight = windowHeight * 0.8;
      const interacWindowWidth = windowWidth * 0.8;

      const left = (windowWidth - interacWindowWidth) / 2;
      const top = windowHeight - interacWindowHeight;
      window.open(
        interac_url,
        "_blank",
        `width=${interacWindowWidth},height=${interacWindowHeight},left=${left},top=${top}`
      );
    }
    setInteracUrl(null);
  }

  const startInteracSession = async () => {
    setInteracUrlLoading(true);
    try {
      const { interac_url } = await fetcher.request<{ interac_url: string }>(
        `/api/internal/applicant/case/${caseId}/non-biometric-idv/start/`,
        {
          method: "POST",
          body: JSON.stringify({
            case_id: caseId,
            language,
          }),
        }
      );
      setInteracUrlLoading(false);
      return interac_url;
    } catch (e) {
      console.error("Error starting Interac session:", e);
      close();
    }
  };

  const maybeLoadInteracUrlAndRedirect = async () => {
    const url = interacUrl || (await startInteracSession());
    url && redirectToInterac(url);
  };

  useEffect(() => {
    if (!interacUrl) {
      startInteracSession().then((url) => {
        url && setInteracUrl(url);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog width={600} aria-label="non-biometric-modal-dialogue">
      {({ close }) => (
        <>
          <DialogHeader onClose={close} testID="non-biometric-modal">
            <Text style="global/modal-header" color="text-weak">
              <Trans>Get started with Interac® verification service</Trans>
            </Text>
          </DialogHeader>
          <DialogContent>
            <Stack gap="space-4">
              <Text style="form/body-primary" color="text-weak">
                <Trans>
                  A new window will open, directing you to an external site with
                  our partner, Interac Verified, to help us verify your
                  identity.
                </Trans>
              </Text>
              <Text style="form/body-primary" color="text-weak">
                <Trans>
                  Please return to this page to submit your application after
                  completing the identity verification process.
                </Trans>
              </Text>
              {optedOutOfBiometricId && (
                <Text style="form/body-primary" color="text-weak">
                  <Trans>
                    Alternatively, you can choose to use the{" "}
                    <Link
                      testID="link-to-document-verification"
                      textStyle="global/body-primary"
                      textColor="text-weak"
                      onPress={optBackInToBiometricId}
                    >
                      document verification method.
                    </Link>
                  </Trans>
                </Text>
              )}
            </Stack>
          </DialogContent>
          <DialogFooter>
            <PrimaryButton
              onPress={maybeLoadInteracUrlAndRedirect}
              size="medium"
              rightIcon="open_in_new"
              isDisabled={interacUrlLoading}
              isLoading={interacUrlLoading}
              type="button"
            >
              <Trans>Start verification</Trans>
            </PrimaryButton>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
